import { useState, useCallback, useMemo } from "react"
import { debounce } from "lodash"
import { useGLTF, useEnvironment, Text } from "@react-three/drei"
import { Select } from "@react-three/postprocessing"
import { Price } from "./Price"

export function Scene(props) {
  const { nodes, materials } = useGLTF("/kitchen-transformed.glb")
  const env = useEnvironment({ preset: "city" })
  const [hovered, hover] = useState(null)
  const debouncedHover = useCallback(debounce(hover, 30), [])
  
  // Define all items in a single object for easy management
  const items = useMemo(() => ({
    ROOM: { price: 5999, name: "ROOM", mesh: null },
    CARPET: { price: 433, name: "CARPET", mesh: nodes.carpet.geometry },
    ROOFLAMP: { 
      price: 77, 
      name: "ROOFLAMP", 
      mesh: [
        { geometry: nodes.lamp_socket.geometry, material: materials.gray },
        { geometry: nodes.lamp.geometry, material: materials.gray },
        { geometry: nodes.lamp_cord.geometry, material: materials.gray }
      ]
    },
    CHAIRS: { 
      price: 255, 
      name: "BLACKLUX CHAIRS", 
      mesh: [
        { geometry: nodes.chairs_1.geometry, material: materials.walls },
        { geometry: nodes.chairs_2.geometry, material: materials.plastic }
      ]
    },
    TABLE: { price: 1699, name: "BIRCH TABLE", mesh: nodes.table.geometry },
    VASE: { price: 44, name: "ROCK VASE", mesh: nodes.vase.geometry }
  }), [nodes, materials])

  const over = (name) => (e) => (e.stopPropagation(), debouncedHover(name))
  const price = items[hovered]?.price ?? items.ROOM.price

  return (
    <>
      <group {...props}>
        {/* Static meshes */}
        <mesh geometry={nodes.vase1.geometry} material={materials.gray} material-envMap={env} />
        <mesh geometry={nodes.bottle.geometry} material={materials.gray} material-envMap={env} />
        <mesh geometry={nodes.walls_1.geometry} material={materials.floor} />
        <mesh geometry={nodes.walls_2.geometry} material={materials.walls} />
        <mesh geometry={nodes.plant_1.geometry} material={materials.potted_plant_01_leaves} />
        <mesh geometry={nodes.plant_2.geometry} material={materials.potted_plant_01_pot} />
        <mesh geometry={nodes.cuttingboard.geometry} material={materials.walls} />
        <mesh geometry={nodes.bowl.geometry} material={materials.walls} />
        <mesh geometry={nodes.kitchen.geometry} material={materials.walls} />
        <mesh geometry={nodes.sink.geometry} material={materials.chrome} material-envMap={env} />

        {/* Dynamic meshes */}
        {Object.entries(items).map(([key, item]) => (
          item.mesh && (
            <Select key={key} enabled={hovered === key} onPointerOver={over(key)} onPointerOut={() => debouncedHover(null)}>
              {Array.isArray(item.mesh) ? (
                item.mesh.map((meshItem, index) => (
                  <mesh 
                    key={index}
                    geometry={meshItem.geometry} 
                    material={meshItem.material} 
                    material-envMap={env}
                    material-envMapIntensity={0.5}
                  />
                ))
              ) : (
                <mesh 
                  geometry={item.mesh} 
                  material={materials.walls} 
                  material-envMap={env}
                  material-envMapIntensity={0.5}
                />
              )}
            </Select>
          )
        ))}
      </group>
      <Text position={[1, 1.25, 0]} color="black" fontSize={0.15} font="Inter-Regular.woff" letterSpacing={-0.05}>
        {hovered ? items[hovered].name : items.ROOM.name}
      </Text>
      <Price value={price} position={[-2, 0.3, -3.25]} />
    </>
  )
}